import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { StoreLayout, StoreHeaderActions, StoreBreadcrumb } from '../../store/StoreLayout';
import { useAppContext } from '../../../context/AppContext';
import { formatPrice } from '../../../utils/storeProducts';
import {
    downloadStoreOrderPdf,
    formatOrderStatus,
    getStoreOrder,
    isOrderPayable,
    refreshStorePayLink,
    resolvePayToken,
    savePayToken,
    syncStorePaymentStatus,
} from '../../../utils/storeApi';

const unwrapOrder = (payload) => payload?.order || payload;

export const OrderDetailPage = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const { token, base_url } = useAppContext();

    const queryPayToken = searchParams.get('pay_token');
    const payToken = resolvePayToken(orderId, queryPayToken);

    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isPaying, setIsPaying] = useState(false);

    const loadOrder = useCallback(
        async ({ syncPayment = false } = {}) => {
            if (!orderId) return;
            if (!token && !payToken) {
                setError('This order link is missing a payment token. Sign in or use the link from your email.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                let detail = null;

                if (syncPayment) {
                    try {
                        const payload = await syncStorePaymentStatus(base_url, {
                            token,
                            orderId,
                            payToken,
                        });
                        if (payload?.pay_token) savePayToken(orderId, payload.pay_token);
                        detail = unwrapOrder(payload);
                    } catch {
                        // Payment sync can fail if Dejavoo is down; still show the order.
                    }
                }

                if (!detail?.id) {
                    detail = unwrapOrder(
                        await getStoreOrder(base_url, { token, orderId, payToken })
                    );
                }

                if (!detail?.id) {
                    throw Object.assign(new Error('Order not found'), { status: 404 });
                }

                setOrder(detail);
            } catch (err) {
                setError(err?.msg || err?.message || 'Unable to load this order.');
                setOrder(null);
            } finally {
                setIsLoading(false);
            }
        },
        [base_url, orderId, payToken, token]
    );

    useEffect(() => {
        // Always reconcile with Dejavoo on landing (HPP return / email deep link).
        loadOrder({ syncPayment: true });
    }, [loadOrder]);

    const handlePayNow = async () => {
        if (!order || isPaying) return;
        setIsPaying(true);

        try {
            let hppUrl = order.hpp_url;
            if (!hppUrl) {
                const response = await refreshStorePayLink(base_url, {
                    token,
                    orderId: order.id,
                    payToken,
                });
                if (response?.pay_token) savePayToken(order.id, response.pay_token);
                hppUrl = response?.hpp_url || response?.order?.hpp_url;
                if (response?.order) setOrder(response.order);
            }

            if (!hppUrl) {
                throw Object.assign(
                    new Error('Payments are temporarily unavailable. Please try again later.'),
                    { status: 503 }
                );
            }

            window.location.assign(hppUrl);
        } catch (err) {
            if (window.showAlert) {
                window.showAlert(err?.status || 500, err?.msg || err?.message || 'Unable to start payment.');
            }
            setIsPaying(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            await downloadStoreOrderPdf(base_url, {
                token,
                orderId,
                payToken,
            });
        } catch (err) {
            if (window.showAlert) {
                window.showAlert(err?.status || 500, err?.msg || err?.message || 'Unable to download receipt.');
            }
        }
    };

    const handleRefreshStatus = async () => {
        setIsRefreshing(true);
        try {
            await loadOrder({ syncPayment: true });
            if (window.showAlert) {
                window.showAlert(200, 'Payment status updated.');
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page">
                <div className="main-content">
                    <StoreLayout>
                        <div className="store-empty-state">
                            <h3>Loading order…</h3>
                        </div>
                    </StoreLayout>
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="page">
                <div className="main-content">
                    <StoreLayout>
                        <div className="page-header">
                            <div className="page-header-row">
                                <h1>Order</h1>
                                {token && <StoreHeaderActions />}
                            </div>
                            <hr />
                        </div>
                        <div className="store-empty-state">
                            <h3>Order unavailable</h3>
                            <p>{error || 'We could not find that order.'}</p>
                            <div className="store-cart-actions" style={{ justifyContent: 'center' }}>
                                {token ? (
                                    <Link to="/store/orders" className="btn btn-primary">
                                        My Orders
                                    </Link>
                                ) : (
                                    <Link
                                        to={`/login?redirect=${encodeURIComponent(`/store/orders/${orderId}`)}`}
                                        className="btn btn-primary"
                                    >
                                        Sign In
                                    </Link>
                                )}
                                <Link to="/store" className="btn btn-secondary">
                                    Back to Store
                                </Link>
                            </div>
                        </div>
                    </StoreLayout>
                </div>
            </div>
        );
    }

    const payable = isOrderPayable(order);
    const customerName = [order.customer_first_name, order.customer_last_name]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="page">
            <div className="main-content">
                <StoreLayout>
                    <div className="page-header">
                        <div className="page-header-row">
                            <h1>Order {order.order_number || `#${order.id}`}</h1>
                            {token && <StoreHeaderActions />}
                        </div>
                        <hr />
                    </div>

                    <StoreBreadcrumb
                        items={[
                            { label: 'Store', path: '/store' },
                            ...(token
                                ? [{ label: 'Orders', path: '/store/orders' }]
                                : []),
                            { label: order.order_number || `Order ${order.id}`, path: null },
                        ]}
                    />

                    <div className="store-order-detail">
                        <div className="store-order-status-banner" data-status={order.status}>
                            <div>
                                <p className="store-order-status-label">Status</p>
                                <h2>{formatOrderStatus(order.status)}</h2>
                            </div>
                            <div className="store-order-status-actions">
                                {payable && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handlePayNow}
                                        disabled={isPaying}
                                    >
                                        {isPaying ? 'Opening payment…' : 'Pay Now'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleRefreshStatus}
                                    disabled={isRefreshing}
                                >
                                    {isRefreshing ? 'Refreshing…' : 'Refresh status'}
                                </button>
                                {(order.has_receipt_pdf || order.status === 'paid') && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleDownloadPdf}
                                    >
                                        Download PDF
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="store-order-meta">
                            <div>
                                <span className="store-meta-label">Customer</span>
                                <span>{customerName || '—'}</span>
                            </div>
                            <div>
                                <span className="store-meta-label">Email</span>
                                <span>{order.customer_email || '—'}</span>
                            </div>
                            <div>
                                <span className="store-meta-label">Phone</span>
                                <span>{order.customer_phone || '—'}</span>
                            </div>
                            <div>
                                <span className="store-meta-label">Placed</span>
                                <span>
                                    {order.created_at
                                        ? new Date(order.created_at).toLocaleString()
                                        : '—'}
                                </span>
                            </div>
                        </div>

                        {order.notes && (
                            <div className="store-order-notes">
                                <h3>Notes</h3>
                                <pre>{order.notes}</pre>
                            </div>
                        )}

                        <table className="store-cart-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Qty</th>
                                    <th>Unit</th>
                                    <th>Line Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(order.items || []).map((item) => (
                                    <tr key={item.id || `${item.sku}-${item.name}`}>
                                        <td>
                                            <strong>{item.name}</strong>
                                            {item.description && (
                                                <div className="store-pack-size">{item.description}</div>
                                            )}
                                        </td>
                                        <td>{item.sku}</td>
                                        <td>{item.quantity}</td>
                                        <td>${formatPrice(item.unit_price)}</td>
                                        <td>${formatPrice(item.line_total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="store-cart-summary">
                            <div className="store-cart-summary-row">
                                <span>Subtotal</span>
                                <span>${formatPrice(order.subtotal)}</span>
                            </div>
                            <div className="store-cart-summary-row">
                                <span>Tax</span>
                                <span>${formatPrice(order.tax)}</span>
                            </div>
                            <div className="store-cart-summary-row total">
                                <span>Total</span>
                                <span>${formatPrice(order.total_amount)}</span>
                            </div>
                        </div>

                        <div className="store-cart-actions">
                            <Link to="/store" className="btn btn-secondary">
                                Continue Shopping
                            </Link>
                            {token && (
                                <Link to="/store/orders" className="btn btn-primary">
                                    My Orders
                                </Link>
                            )}
                        </div>
                    </div>
                </StoreLayout>
            </div>
        </div>
    );
};
